import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Spinner, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ControlledMenu, MenuItem, SubMenu } from "@szhsin/react-menu";
import { Button, Form } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Rect } from "react-konva";
import { matchApi } from "../../api";
import Preloader from "@/components/Elements/Preloader";
import ProgressBar from "./ProgressBar";
import VideoPlayer from "@/components/VideoPlayer";
import SuperTracksPanel from "./SuperTracksPanel";
import { getFormattedDuration } from "@/util/video-player";
import {
  generateSuperTrackMapV2,
  countLargeGaps,
  isJsonString,
  getPriorityWeight,
  cn,
  isTimeInRange,
} from "@/util/functions";
import { LABELER } from "@/constants/routs.constants";

import "./SuperTrackMarkup.scss";
import { useSuperTracks } from "@/hooks/useSuperTracks";
import useTask from "@/hooks/useTask";
import "@szhsin/react-menu/dist/index.css";
import { CUT_MODES } from "@/constants";
import { useActions } from "@/hooks/useActions";
import {
  addSuperTrack,
  appendFrameBoxes,
  cutSuperTrack,
  deleteSuperTrack,
  deleteTrack,
  mergeSuperTrack,
  moveTracks,
  setBoxes,
  setChangedKeyFrames,
  setFrameBoxes,
  setFrameCount,
  setNewBoxes,
  setSuperTrackEndTime,
  setSuperTrackGlobalVideoDuration,
  setSuperTracks,
  setSuperTrackStartTime,
  updateError,
  updateSuperTrack,
} from "@/store/supertrackSlice";
import { isEqual } from "lodash";
import Switch from "@/components/Elements/switch";
import MyButton from "@/components/Elements/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HourGlassButton from "@/components/Elements/HourGlassButton";
import { resetSaveFunction } from "@/store/communicationSlice";
import * as Sentry from "@sentry/react";

export default function SuperTrackMarkup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { videoId, trackId } = useParams();

  const dispatch = useDispatch();

  // #region custom hooks
  const { hasIntersection } = useSuperTracks();
  const { taskId, taskType, frameStart, frameEnd } = useTask();
  const { merge, cut, replace, substitute } = useActions();
  // #endregion custom hooks

  const {
    superTracks,
    trackBoxes,
    isCutActive,
    cutFrame,
    boxes,
    newBoxes,
    changedKeyFrames,
  } = useSelector(({ superTracks }) => superTracks);

  const { user } = useSelector(({ auth }) => auth);

  const triggerSave = useSelector((state) => state.communication.triggerSave);

  // #region Refs definition
  const videoRef = useRef();
  const newRectRef = useRef();
  const newRect = useRef({
    visible: false,
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });
  const percentRef = useRef();
  const posRef = useRef();
  const menuRef = useRef();
  const isCutting = useRef(false);
  const realtimeSync = useRef(true);
  const storingRef = useRef(false);
  const newBoxesRef = useRef({});
  const changedKeyFramesRef = useRef([]);
  // #endregion Refs definition
  // #region States definition
  const [videoPath, setVideoPath] = useState("");
  const [currentFrame, setCurrentFrame] = useState(0);
  const [frameTime, setFrameTime] = useState(0);
  const [videoDomWidth, setVideoDomWidth] = useState(0);
  const [videoDomHeight, setVideoDomHeight] = useState(0);
  const [videoWidth, setVideoWidth] = useState(0);
  const [videoHeight, setVideoHeight] = useState(0);
  const [ratio, setRatio] = useState(1);
  const [step, setStep] = useState(30);
  const [videoDuration, setVideoDuration] = useState(0);
  const [stageScale, setStageScale] = useState(1);
  const [stageX, setStageX] = useState(0);
  const [stageY, setStageY] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalFrames, setTotalFrames] = useState(0);
  const [interpol, setInterpol] = useState(true);
  const [interpolStart, setInterpolStart] = useState(null);
  const [interpolEnd, setInterpolEnd] = useState(null);
  const [overwriteMode, setOverwriteMode] = useState(false);
  const [fps, setFps] = useState(0);
  const [lastUpdatedFrame, setLastUpdatedFrame] = useState(null);
  const [changedFrameCnt, setChangedFrameCnt] = useState(0);
  const [changedFrames, setChangedFrames] = useState([]);
  const [candidates, setCandidates] = useState({});
  const [frameStep, setFrameStep] = useState(1);
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [selectedSuperTrackId, setSelectedSuperTrackId] = useState(null);
  const [currentSuperTrack, setCurrentSuperTrack] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [visibleSuperTrackIds, setVisibleSuperTrackIds] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(); // This is used for cutting
  const [imageY, setImageY] = useState(0);
  const [isStoring, setIsStoring] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [searchRange, setSearchRange] = useState(180);
  const [gapCounts, setGapCounts] = useState(0);
  const [trackIds, setTrackIds] = useState({});
  const [colors, setColors] = useState({});
  const [autoZoom, setAutoZoom] = useState(false);
  const [realTimeSync, setRealTimeSync] = useState(true);
  const [stopFrame, setStopFrame] = useState(null);
  const [pbRate, setPbRate] = useState(null);
  const [tempRectActive, setTempRectActive] = useState(false); // Indicate whether the rect is activated or not
  const [progressTracks, setProgressTracks] = useState([]);
  const [gamePausedOut, setGamePausedOut] = useState(null);
  const [gamePausedInactive, setGamePausedInactive] = useState(null);
  const [pauseList, setPauseList] = useState([]);
  const [allowed, setAllowed] = useState(true);
  const [superTrackType, setSuperTrackType] = useState(null);
  const [allowPauseEditing, setAllowPauseEditing] = useState(false);
  const [jersey, setJersey] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingMergeAction, setPendingMergeAction] = useState(null);
  const [isBallRectMoving, setIsBallRectMoving] = useState(false);
  const [ballRectOffset, setBallRectOffset] = useState({ x: 0, y: 0 });
  const [autoNext, setAutoNext] = useState(true);
  // #endregion States definition

  const memoizedBoxes = useMemo(
    () => ({ main: boxes, ...candidates }),
    [boxes, candidates]
  );

  useEffect(() => {
    if (triggerSave) {
      console.log("triggerSave", triggerSave);
      saveAnswer(triggerSave);
      dispatch(resetSaveFunction());
    }
  }, [triggerSave]);

  // Websocket handler for data synchronization across labelers
  useEffect(() => {
    let socket = null;

    const createWebSocketConnection = () => {
      socket = new WebSocket(
        `${process.env.REACT_APP_PANEL_WS}match/${videoId}/super-tracks/`
      );

      socket.onopen = () => {
        console.log("WebSocket connection established.");
        const token = localStorage.getItem("access");
        socket.send(token);
        toast.success("WebSocket connection established", {
          position: "top-center",
          autoClose: 2000,
        });
      };

      socket.onmessage = (event) => {
        if (realtimeSync.current) {
          if (isJsonString(event.data)) {
            const parsedData = JSON.parse(event.data);
            switch (parsedData.operation) {
              case "create": {
                const { super_track } = parsedData;
                dispatch(addSuperTrack(super_track));
                break;
              }
              case "merge": {
                const { new_super_track_uid, old_super_track_uid } = parsedData;
                dispatch(
                  mergeSuperTrack({
                    targetId: new_super_track_uid,
                    superTrackId: old_super_track_uid,
                  })
                );
                break;
              }
              case "delete": {
                const { super_track_uid } = parsedData;
                dispatch(deleteSuperTrack(super_track_uid));
                break;
              }
              case "delete_track": {
                const { track_uid, super_track_uid } = parsedData;
                dispatch(
                  deleteTrack({
                    track_id: track_uid,
                    superTrackId: super_track_uid,
                  })
                );
                break;
              }
              case "cut": {
                const {
                  super_track_uid,
                  response_data: { supertrack, tracks },
                } = parsedData;
                dispatch(
                  updateSuperTrack({
                    superTrackId: super_track_uid,
                    tracks: supertrack,
                  })
                );
                dispatch(appendFrameBoxes({ newBoxes: tracks }));
                break;
              }
              case "replace": {
                const { new_super_track_uid, super_track_uid, tracks_ids } =
                  parsedData;
                dispatch(
                  moveTracks({
                    fromId: super_track_uid,
                    toId: new_super_track_uid,
                    trackIds: tracks_ids,
                  })
                );
                break;
              }
              case "update": {
                const { super_track_uid, supertrack, tracks } = parsedData;
                dispatch(
                  updateSuperTrack({
                    superTrackId: super_track_uid,
                    tracks: supertrack,
                  })
                );
                dispatch(appendFrameBoxes({ newBoxes: tracks }));
                break;
              }
              default: {
                break;
              }
            }
          }
        }
      };

      socket.onclose = () => {
        console.log("WebSocket connection closed. Reconnecting...");
        setTimeout(() => {
          createWebSocketConnection();
        }, 5000);
      };
    };

    createWebSocketConnection();

    return () => {
      if (socket) {
        socket.onclose = function () { };
        socket.close();
        socket = null;
      }
    };
  }, []);

  // To get combined boxes from orignal db boxes and newly boxes
  // Combined boxes are used to prevent boxes from being cleared when supertrack data changes(like when merging or cutting)
  useEffect(() => {
    if (!superTracks || !trackBoxes) return;

    console.time("combineBoxes1");
    const currentSt = superTracks[trackId];
    if (!currentSt?.tracks || !Array.isArray(currentSt.tracks)) return;

    let originalBoxes = {};
    for (const track of currentSt.tracks) {
      const { track_id } = track;
      Object.assign(originalBoxes, trackBoxes[track_id]);
      // originalBoxes = { ...originalBoxes, ...trackBoxes[track_id] };
    }
    console.timeEnd("combineBoxes1");
    console.time("combineBoxes2");
    // const boxes2 = { ...originalBoxes, ...newBoxes };
    Object.assign(originalBoxes, newBoxes);
    console.timeEnd("combineBoxes2");
    if (isEqual(boxes, originalBoxes)) {
      return; // means there's no change
    }
    dispatch(setBoxes(originalBoxes));
  }, [superTracks, trackBoxes, newBoxes]);

  useEffect(() => {
    setGapCounts(countLargeGaps(Object.keys(boxes), step, totalFrames));
  }, [boxes, step, totalFrames]);

  useEffect(() => {
    if (!superTracks || !trackBoxes) return;

    const currentSt = superTracks[trackId];
    if (!currentSt?.tracks || !Array.isArray(currentSt.tracks)) return;
    let flag = false;
    if (currentSuperTrack && Array.isArray(currentSuperTrack.tracks)) {
      // Check if there are any changes in current super track
      if (
        currentSuperTrack.id !== currentSt.id ||
        currentSuperTrack.tracks?.length !== currentSt.tracks?.length
      )
        flag = true;
      else
        for (let i = 0; i < currentSt.tracks.length; i++) {
          const {
            track_id: track_id1,
            frame_start: frame_start1,
            frame_end: frame_end1,
          } = currentSt.tracks[i];
          const { track_id, frame_start, frame_end } =
            currentSuperTrack.tracks[i];
          if (
            track_id !== track_id1 ||
            frame_start !== frame_start1 ||
            frame_end !== frame_end1
          )
            flag = true;
        }
    } else flag = true;

    if (flag === false) {
      return; // means there's no change in current supertrack
    }

    setCurrentSuperTrack(currentSt);
  }, [superTracks, trackBoxes]);

  useEffect(() => {
    if (!videoId || !trackId) return;

    async function fetchMatchInfo() {
      try {
        const match = await matchApi.getMatch(videoId);
        if (!match) return;

        const { fps, origin } = match.video;
        setFps(fps);
        setFrameTime(1 / fps);
        setVideoPath(origin);

        const { pipeline_passing } = match;
        const isBroadcastingPipeline =
          pipeline_passing === "broadcasting" ||
          pipeline_passing === "broadcasting_draw";

        // fetch superTracks from db
        const res = await matchApi.getMatchSuperTracksFull(videoId);
        const { frame_step, supertracks, tracks, pauses } = res;
        if (!supertracks || !tracks) return;

        // Set SuperTrack Type
        setSuperTrackType(supertracks[trackId]?.kind);
        if (supertracks[trackId]?.kind === "ball") {
          setStep(isBroadcastingPipeline ? 30 : 15);
        }

        if (pauses && pauses.length > 0) {
          setPauseList(
            pauses.map(({ start, end, type }) => [start, end, type])
          );
        }
        // saved all coords with trackIds. this data is used for finding candidates
        dispatch(setFrameBoxes(tracks));
        if (frame_step) setFrameStep(frame_step);

        dispatch(setSuperTracks(generateSuperTrackMapV2(supertracks)));
        setDataLoaded(true);
      } catch (error) {
        Sentry.captureException(error); // Report to Sentry
        console.log(error);
        dispatch(updateError(error));
      }
    }

    fetchMatchInfo();
  }, [videoId, trackId]);

  useEffect(() => {
    if (superTrackType === "ball" && user?.is_staff) {
      setAllowPauseEditing(true);
    }
  }, [superTrackType, user]);

  // Find candidates
  useEffect(() => {
    if (!superTracks || !trackBoxes) return;
    // find the current super track
    const mainSuperTrack = superTracks[trackId];

    console.log("length", Object.keys(superTracks).length);

    if (!mainSuperTrack) {
      toast.error(`Main supertrack (${trackId}) data does not exist.`);
      return;
    }

    if (!mainSuperTrack.tracks || !Array.isArray(mainSuperTrack.tracks)) {
      toast.error(`Main supertrack (${trackId}) has no valid tracks.`);
      return;
    }

    let filteredStIds = [];
    let lastTrackId;
    let lastFrame = 0;
    mainSuperTrack.tracks.forEach(({ track_id, frame_end }) => {
      if (frame_end > lastFrame) {
        lastFrame = frame_end;
        lastTrackId = track_id;
      }
    });
    if (!showAll) {
      // find the candidates
      for (let id in superTracks) {
        const superTrack = superTracks[id];
        if (!superTrack || !superTrack.tracks || !Array.isArray(superTrack.tracks)) {
          continue; // Skip empty or invalid supertracks
        }
        const { tracks } = superTrack;
        const isCandidate = tracks.every(
          (td) =>
            td.frame_start > lastFrame - 5 &&
            td.frame_end < lastFrame + searchRange * fps
        );
        if (isCandidate) filteredStIds.push(id);
      }
    } else {
      filteredStIds = Object.keys(superTracks).filter(
        (stId) => stId != trackId
      );
    }
    if (!isEqual(filteredStIds, visibleSuperTrackIds))
      setVisibleSuperTrackIds([...filteredStIds]);

    // gather all trackIds of candidate supertracks
    let filteredTracks = [];
    for (const id of filteredStIds) {
      const superTrack = superTracks[id];
      if (superTrack && superTrack.tracks && Array.isArray(superTrack.tracks)) {
        filteredTracks = [...filteredTracks, ...superTrack.tracks];
      }
    }

    if (trackBoxes[lastTrackId]) {
      const coords = Object.entries(trackBoxes[lastTrackId]).at(-1)[1]; // The last frame coords of main supertrack
      filteredTracks.sort((a, b) => {
        if (trackBoxes[a.track_id] && trackBoxes[b.track_id]) {
          const coordsA = Object.entries(trackBoxes[a.track_id])[0][1];
          const coordsB = Object.entries(trackBoxes[b.track_id])[0][1];
          const maxDistance = Math.sqrt(
            videoWidth * videoWidth + videoHeight * videoHeight
          );
          return (
            getPriorityWeight(
              coords,
              coordsA,
              maxDistance,
              a.frame_start - lastFrame,
              totalFrames
            ) -
            getPriorityWeight(
              coords,
              coordsB,
              maxDistance,
              b.frame_start - lastFrame,
              totalFrames
            )
          );
        } else {
          return false;
        }
      });
    }

    let candidateTracks = {};
    const newTrackIds = {};
    filteredTracks.forEach((track, index) => {
      if (!track || typeof track.track_id === "undefined") return;
      candidateTracks[track.track_id] = trackBoxes[track.track_id];
      if (index < 9) newTrackIds[track.track_id] = index + 1;
    });

    if (!isEqual(newTrackIds, trackIds)) setTrackIds({ ...newTrackIds });
    // saved all candidate tracks coords with trackIds
    if (!isEqual(candidates, candidateTracks)) {
      setCandidates(candidateTracks);
    }
  }, [superTracks, trackBoxes, showAll, fps, searchRange]);

  useEffect(() => {
    const notAllowed = isTimeInRange(currentFrame, pauseList);
    setAllowed(!notAllowed);

    if (interpol) {
      setInterpolStart(null);
      setInterpolEnd(null);
    }

    if (
      stopFrame !== null &&
      currentFrame > stopFrame &&
      !videoRef?.current?.paused
    ) {
      videoRef.current?.pause();
      if (videoRef.current)
        videoRef.current.currentTime = frameTime * stopFrame;
      setCurrentFrame(stopFrame);
      setPbRate(1);
      setStopFrame(null);
    }
  }, [currentFrame]);

  useEffect(() => {
    if (videoDuration && frameTime) {
      setTotalFrames(Number(Math.round(videoDuration / frameTime)));
    }
  }, [videoDuration, frameTime]);

  function onTrackClicked(trackIndex) {
    const trackId = Object.keys(trackIds).find(
      (trackId) => Number(trackIds[trackId]) === Number(trackIndex)
    );
    setColors({ [trackId]: "yellow" });
    let superTrackId;
    for (let id in superTracks) {
      const superTrack = superTracks[id];
      if (!superTrack || !superTrack.tracks || !Array.isArray(superTrack.tracks)) {
        continue; // Skip empty or invalid supertracks
      }
      const exists = superTrack.tracks.find(
        (track) => Number(track.track_id) === Number(trackId)
      );
      if (exists) {
        superTrackId = id;
        break;
      }
    }
    setSelectedSuperTrackId(superTrackId);
  }

  const keyDownHandler = (event) => {
    if (/^[1-9]$/.test(event.key)) {
      onTrackClicked(event.key);
    } else if (event.ctrlKey && event.key === "s") {
      event.preventDefault();
      store();
    } else if (event.key === "w") {
      event.preventDefault();
      handleNextMissedStep();
    } else if (event.key === "Enter" || event.key === "r") {
      if (selectedSuperTrackId === null) return;
      onMerge();
      setSelectedSuperTrackId(null);
    } else if (event.key === "q") {
      handleNextMissed();
    } else if (event.key === "a") {
      setInterpol(!interpol);
    }
  };

  // #region Navigation handlers
  const handleNextMissed = () => {
    const videoElement = videoRef.current;
    if (!boxes || !totalFrames || !videoElement) return;
    for (let i = currentFrame + 1; i < totalFrames; i++) {
      if (!boxes[i]) {
        videoElement.currentTime += frameTime * (i - currentFrame);
        break;
      }
    }
  };

  const findStopFrame = (currentFrame, allowNavigation = false) => {
    const videoElement = videoRef.current;
    if (!boxes || !totalFrames || !videoElement) return;

    let count = 0;
    let lastFrame = null;
    for (let i = currentFrame + 1; i < totalFrames; i++) {
      if (!boxes[i]) {
        count++;
        if (count >= step) {
          if (lastFrame !== null && lastFrame - step > currentFrame) {
            const targetFrame = lastFrame - step;
            setStopFrame(Math.min(targetFrame + step * 2, totalFrames));
          } else {
            setStopFrame(i);
          }
          setPbRate(3);
          videoRef.current?.play();
          break;
        }
      } else {
        count = 0;
        lastFrame = i;
      }
    }
  };

  const handleNextMissedStep = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    findStopFrame(currentFrame, false);
  };
  // #endregion Navigation handlers

  const onStepChange = (e) => {
    setStep(Number(e.target.value));
  };
  const onSearchRangeChange = (e) => {
    if (isNaN(e.target.value)) return;

    if (Number(e.target.value < 0)) e.target.value = 0;
    if (Number(e.target.value > 240)) e.target.value = 240;
    setSearchRange(Number(e.target.value));
  };
  const onFrameInputChange = (e) => {
    const value = Number(e.target.value);
    if (value >= 0 && value <= totalFrames) {
      setCurrentFrame(value);
      if (videoRef.current) videoRef.current.currentTime = value * frameTime;
    }
  };

  //TODO: review
  const updateCoords = (coords, interpolOn = false) => {
    let keyFrames;
    if (storingRef.current === true) {
      changedKeyFramesRef.current = [
        ...changedKeyFramesRef.current,
        currentFrame,
      ];
    } else {
      keyFrames = [
        ...changedKeyFrames.filter((frame) => frame !== currentFrame),
        currentFrame,
      ];
      dispatch(setChangedKeyFrames(keyFrames));
    }
    let newCoords = coords;
    if (imageY) {
      newCoords = coords.map((val, i) =>
        [1, 3].includes(i) ? val - imageY : val
      );
    }
    let clonedNewBoxes;
    if (storingRef.current === true) {
      clonedNewBoxes = { ...newBoxesRef.current };
      clonedNewBoxes[currentFrame] = newCoords;
      newBoxesRef.current = { ...clonedNewBoxes };
      dispatch(setBoxes({ ...boxes, ...newBoxesRef.current }));
    } else {
      console.time("updateCoords");
      clonedNewBoxes = { ...newBoxes };
      console.timeEnd("updateCoords");
      clonedNewBoxes[currentFrame] = newCoords;
      dispatch(setNewBoxes({ ...clonedNewBoxes }));
    }
    console.time("interpolate");
    if (interpol && interpolOn) {
      // Save changed frame numbers to use them on overwrite mode.
      if (!changedFrames.includes(currentFrame)) {
        const sorted = [...changedFrames, currentFrame].sort((a, b) => a - b);
        setChangedFrames([...sorted]);
      }
      if (interpolStart !== null || interpolEnd !== null) {
        // interpolStart !== null means the frame hasn't changed after the last interpolation so reapply interpolation on the same frame.
        interpolate(
          newCoords,
          interpolStart,
          currentFrame,
          interpolEnd,
          clonedNewBoxes,
          (newBoxes) => callStore(newBoxes, keyFrames)
        );
      } else {
        // if it's the first time of editing(changedFrames.length > 0) while turning on the overwrite mode
        // we apply the normal mode
        if (overwriteMode && changedFrames.length > 0) {
          let start = null,
            end = null;
          for (let i = 0; i < changedFrames.length; i++) {
            if (changedFrames[i] < currentFrame) {
              start = changedFrames[i];
            } else if (changedFrames[i] > currentFrame) {
              end = changedFrames[i];
              break;
            }
          }
          if (start !== null || end !== null) {
            setInterpolStart(start);
            setInterpolEnd(end);
            interpolate(
              newCoords,
              start,
              currentFrame,
              end,
              clonedNewBoxes,
              (newBoxes) => callStore(newBoxes, keyFrames)
            );
          }
        } else {
          for (let i = currentFrame - 1; i >= frameStart ?? 0; i--) {
            if (isTimeInRange(i, pauseList)) {
              break;
            }
            if (boxes[i] && currentFrame - i <= Math.max(step, 200)) {
              setInterpolStart(i);
              interpolate(
                newCoords,
                i,
                currentFrame,
                null,
                clonedNewBoxes,
                (newBoxes) => callStore(newBoxes, keyFrames)
              );
              break;
            }
          }
        }
      }
    }
    console.timeEnd("interpolate");
  };

  const callStore = (newBoxes, keyFrames) => {
    if (lastUpdatedFrame !== currentFrame) {
      // Store changes at every 20 frames.
      if (changedFrameCnt === 100) {
        store(newBoxes, keyFrames);
      }
      setChangedFrameCnt((prev) => prev + 1);

      setLastUpdatedFrame(currentFrame);
    }
  };

  /**
   * Applies the interpolation from start to target and target to end frame.
   * @param {Array<Number>} newCoords Coords on the current frame
   * @param {Number} start Start frame number of the interpolation
   * @param {Number} target Target frame number of the interpolation
   * @param {Number} end End frame number of the interpolation
   * @param {Object} defaultNewBoxes Default newBoxes object to apply the interpolation
   * @returns
   */
  const interpolate = (
    newCoords,
    start = null,
    target,
    end = null,
    defaultNewBoxes,
    callback
  ) => {
    const clonedNewBoxes = defaultNewBoxes || { ...newBoxes };
    if (start !== null) {
      const frameDiff = Math.abs(target - start);
      if (frameDiff < 2 || !boxes[start]) return;
      let coordsDiff = [];
      for (let i = 0; i < 4; i++) {
        coordsDiff[i] = (newCoords[i] - boxes[start][i]) / frameDiff;
      }
      for (let i = start + 1; i < target; i++) {
        clonedNewBoxes[i] = [];
        for (let j = 0; j < 4; j++) {
          clonedNewBoxes[i][j] = Math.round(
            boxes[start][j] + coordsDiff[j] * (i - start)
          );
        }
      }
    }
    if (end !== null) {
      const frameDiff = Math.abs(end - target);
      if (frameDiff < 2 || !boxes[end]) return;
      let coordsDiff = [];
      for (let i = 0; i < 4; i++) {
        coordsDiff[i] = (boxes[end][i] - newCoords[i]) / frameDiff;
      }
      for (let i = target + 1; i < end; i++) {
        clonedNewBoxes[i] = [];
        for (let j = 0; j < 4; j++) {
          clonedNewBoxes[i][j] = Math.round(
            boxes[target][j] + coordsDiff[j] * (i - target)
          );
        }
      }
    }

    if (storingRef.current === true) {
      newBoxesRef.current = { ...clonedNewBoxes };
      dispatch(setBoxes({ ...boxes, ...newBoxesRef.current }));
    } else {
      dispatch(setNewBoxes({ ...clonedNewBoxes }));
    }
    callback(clonedNewBoxes);
  };

  // triggered when the rect is moved or resized
  // we pass down trackResizable prop to VideoPlayer
  const onResizeRect = (trackId, { x, y, width, height }) => {
    if (!allowed) {
      console.log("not allowed");
      return;
    }

    if (tempRectActive) {
      return setTempRectActive(false);
    }

    const newCoords = [
      Math.round(x / ratio),
      Math.round(y / ratio),
      Math.round((x + width) / ratio),
      Math.round((y + height) / ratio),
    ];
    updateCoords(newCoords, true);
  };

  const store = async (
    boxes = null,
    keyFrames = null,
    substitutionId = null
  ) => {
    try {
      setIsStoring(true);
      storingRef.current = true;
      const pauses = pauseList.map(([start, end, type]) => ({
        start,
        end,
        type,
      }));
      const { supertrack, tracks } = await matchApi.updateSuperTrackFrames(
        videoId,
        trackId,
        boxes || newBoxes,
        keyFrames?.length ? keyFrames : changedKeyFrames,
        taskId ? taskId : null,
        [frameStart, frameEnd],
        pauses,
        substitutionId
      );
      dispatch(setChangedKeyFrames([...changedKeyFramesRef.current]));
      if (Object.keys(newBoxesRef.current).length > 0) {
        dispatch(setNewBoxes({ ...newBoxesRef.current }));
        dispatch(appendFrameBoxes({ newBoxes: tracks }));
        dispatch(
          updateSuperTrack({
            superTrackId: trackId,
            tracks: supertrack,
          })
        );
      } else {
        dispatch(appendFrameBoxes({ newBoxes: tracks }));
        dispatch(
          updateSuperTrack({ superTrackId: trackId, tracks: supertrack })
        );
        dispatch(setNewBoxes({}));
      }
      storingRef.current = false;
      newBoxesRef.current = {};
      setChangedFrameCnt(changedKeyFramesRef.current?.length || 0);
      changedKeyFramesRef.current = [];
      toast.success("Storing Success.");
    } catch (err) {
      Sentry.captureException(err); // Report to Sentry
      console.log(err);
      const errorMessage =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data)?.slice(0, 200) ||
        err.message ||
        "Something went wrong.";
      toast(errorMessage);
    } finally {
      setIsStoring(false);
    }
  };

  const saveAnswer = async (substitutionId = null) => {
    try {
      setIsSaving(true);
      await store(null, null, substitutionId);
      const query = new URLSearchParams(location.search);
      const taskId = query.get("taskId");
      if (!taskId) {
        toast.warning("taskId is not specificed.");
        return;
      }

      await matchApi.postMatchTaskResult({
        matchId: videoId,
        taskId: taskId,
        taskResult: {
          status: true,
          pauses: pauseList.map(([start, end, type]) => ({
            start,
            end,
            type,
          })),
        },
        taskType,
      });
      toast.success("Saved the answer successfully.");
      navigate(LABELER);
    } catch (err) {
      console.log(err);
      toast(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // this is called when drawing new rect
  const updateSelectionRect = () => {
    if (!allowed) {
      console.log("updateSelectionRect: drawing now allowed");
      return;
    }

    const node = newRectRef.current;
    node.setAttrs({
      visible: newRect.current.visible,
      x:
        (Math.min(newRect.current.x1, newRect.current.x2) - stageX) /
        stageScale,
      y:
        (Math.min(newRect.current.y1, newRect.current.y2) - stageY) /
        stageScale,
      width: Math.abs(newRect.current.x1 - newRect.current.x2) / stageScale,
      height: Math.abs(newRect.current.y1 - newRect.current.y2) / stageScale,
      stroke: "red",
      strokeWidth: 1 / stageScale,
      opacity: 0.6,
    });
    node.getLayer().batchDraw();
  };

  // #region Mouse event handlers
  const BALL_SIZE = 16; // Size of the ball rectangle

  const onMouseDownHandler = (e) => {
    // dragging with Ctrl+LMB
    if (e.evt.button === 0 && e.evt.ctrlKey) return;

    // RMB: Right Mouse Button
    if (e.evt.button === 2) {
      // Clicked on the Empty space
      if (!["rect", "text"].includes(e.target?.attrs.name)) {
        // if we are on empty place of the stage we will do nothing
        setMenuVisible(false);
        setSelectedTrackId(null);
        return;
      }
      let { trackId } = e.target.attrs;
      if (trackId === "main") {
        if (currentSuperTrack) {
          currentSuperTrack.tracks.forEach((t) => {
            const { track_id, frame_start, frame_end } = t;
            if (frame_start <= currentFrame && frame_end >= currentFrame)
              trackId = track_id;
          });
        }
      } else {
        setSelectedTrackId(trackId);
      }

      let superTrackId, existingTrack;
      for (let id in superTracks) {
        const superTrack = superTracks[id];
        if (!superTrack || !superTrack.tracks || !Array.isArray(superTrack.tracks)) {
          continue; // Skip empty or invalid supertracks
        }
        existingTrack = superTrack.tracks.find(
          (track) => Number(track.track_id) === Number(trackId)
        );
        if (existingTrack) {
          superTrackId = id;
          setSelectedTrack(existingTrack);
          break;
        }
      }

      if (existingTrack === undefined) {
        setSelectedTrack(undefined);
      }

      // TODO: if we press enter, it will merge this supertrack. We need to differentiate RMB & Shortkey merging.
      setSelectedSuperTrackId(superTrackId);

      const stage = e.target.getStage();
      // show menu
      setMenuVisible(true);
      menuRef.current.style.top = stage.getPointerPosition().y + 4 + "px";
      menuRef.current.style.left = stage.getPointerPosition().x + 16 + "px";
    }

    if (!allowed) {
      console.log("onMouseDownHandler: drawing now allowed");
      return;
    }

    if (e.target.attrs.name === "tempRect") return;

    let drawMode = false;
    if (
      e.target.attrs.name === "rect" ||
      e.target.attrs.name === "text" ||
      (e.target.attrs.name?.includes && e.target.attrs.name.includes("_anchor"))
    ) {
      // switch to selection mode
      setIsDrawing(false);
    } else {
      drawMode = true;
      // switch to draw mode
      if (e.evt.ctrlKey) return; // don't draw if we are in merge mode
      setIsDrawing(true);
    }

    // don't draw if it's element or transformer
    const isElement = e.target.findAncestor(".elements-container");
    const isTransformer = e.target.findAncestor("Transformer");
    if (isElement || isTransformer) {
      return;
    }

    console.log("isElement, isTransformer", isElement, isTransformer);

    const isPointInBallRect = (x, y) => {
      if (!newRect.current.visible) return false;
      const left = Math.min(newRect.current.x1, newRect.current.x2);
      const top = Math.min(newRect.current.y1, newRect.current.y2);
      const right = Math.max(newRect.current.x1, newRect.current.x2);
      const bottom = Math.max(newRect.current.y1, newRect.current.y2);
      return x >= left && x <= right && y >= top && y <= bottom;
    };

    // --- Ball supertrack: click-to-place logic ---
    if (superTrackType === "ball" && drawMode) {
      // Only respond to left mouse button
      if (e.evt.button === 0) {
        const pos = e.target.getStage().getPointerPosition();
        if (newRect.current.visible && isPointInBallRect(pos.x, pos.y)) {
          // Start moving the ball rect
          setIsBallRectMoving(true);
          setBallRectOffset({
            x: pos.x - newRect.current.x1,
            y: pos.y - newRect.current.y1,
          });
        } else {
          // Place new ball rect
          newRect.current.visible = true;
          newRect.current.x1 = pos.x - BALL_SIZE / 2;
          newRect.current.y1 = pos.y - BALL_SIZE / 2;
          newRect.current.x2 = pos.x + BALL_SIZE / 2;
          newRect.current.y2 = pos.y + BALL_SIZE / 2;
          updateSelectionRect();
          // Immediately start moving mode after placing
          setIsBallRectMoving(true);
          setBallRectOffset({ x: BALL_SIZE / 2, y: BALL_SIZE / 2 });
        }
      }
      return; // Don't start drag for ball
    }

    // start drawing new rect
    const pos = e.target.getStage().getPointerPosition();
    newRect.current.visible = true;
    newRect.current.x1 = pos.x;
    newRect.current.y1 = pos.y;
    newRect.current.x2 = pos.x;
    newRect.current.y2 = pos.y;
    updateSelectionRect();
  };

  const onMouseMoveHandler = (e) => {
    if (e.evt.button === 2) return;
    if (!allowed) return;

    const pos = e.target.getStage().getPointerPosition();

    // Ball rect move logic
    if (
      superTrackType === "ball" &&
      isBallRectMoving &&
      newRect.current.visible
    ) {
      // Move the rect to follow the mouse, keeping the offset
      newRect.current.x1 = pos.x - ballRectOffset.x;
      newRect.current.y1 = pos.y - ballRectOffset.y;
      newRect.current.x2 = newRect.current.x1 + BALL_SIZE;
      newRect.current.y2 = newRect.current.y1 + BALL_SIZE;
      updateSelectionRect();
      return;
    }

    if (!newRect.current.visible || !isDrawing) {
      return;
    }

    newRect.current.x2 = pos.x;
    newRect.current.y2 = pos.y;
    updateSelectionRect();
  };

  const onMouseUpHandler = (e) => {
    if (e.evt.button === 2) return;

    if (!allowed) return;

    if (e.target.attrs.name === "tempRect") {
      const { x, y, width, height } = e.target.attrs;
      const newCoords = [
        Math.round(x / ratio),
        Math.round(y / ratio),
        Math.round((x + width) / ratio),
        Math.round((y + height) / ratio),
      ];
      updateCoords(newCoords, true);
      setTempRectActive(true);
      return;
    }

    if (!isDrawing) {
      return;
    }
    newRect.current.visible = false;
    setIsDrawing(false);
    const { x1, x2, y1, y2 } = newRect.current;
    const moved = x1 !== x2 || y1 !== y2;
    if (!moved) {
      updateSelectionRect();
      return;
    }
    const { x, y, width, height } = newRectRef.current.attrs;
    const newCoords = [
      Math.round(x / ratio),
      Math.round(y / ratio),
      Math.round((x + width) / ratio),
      Math.round((y + height) / ratio),
    ];
    updateCoords(newCoords, true);
    updateSelectionRect();

    // Ball rect move logic
    if (superTrackType === "ball" && isBallRectMoving) {
      setIsBallRectMoving(false);
      if (autoNext) {
        handleNextMissedStep();
      }
      return;
    }
  };
  // #endregion Mouse event handlers

  const onJerseyUpdate = useCallback(
    async (e) => {
      const jersey = e.target.value || null;
      await matchApi.postSuperTrackJersey({
        matchId: videoId,
        superTracksId: trackId,
        jersey,
      });
    },
    [videoId, trackId]
  );

  const onJerseyChange = useCallback((e) => {
    setJersey(e.target.value);
  }, []);

  const onFrameChange = useCallback((frameNumber) => {
    setCurrentFrame(frameNumber);
  }, []);

  const onStageScaleChange = useCallback((newStageScale) => {
    setStageScale(newStageScale);
  }, []);

  const onStagePosChange = useCallback((newStageX, newStageY) => {
    setStageX(newStageX);
    setStageY(newStageY);
  }, []);

  const onRatioChange = useCallback((newRatio) => {
    setRatio(newRatio);
  }, []);

  const onVideoSizeChange = useCallback((width, height) => {
    setVideoWidth(width);
    setVideoHeight(height);
  }, []);

  const onVideoDomSizeChange = useCallback((width, height) => {
    setVideoDomWidth(width);
    setVideoDomHeight(height);
  }, []);

  const onSetInitialY = useCallback((y) => {
    setImageY(y);
  }, []);

  const onProgressClick = useCallback(
    (e) => {
      const rect = percentRef.current?.getBoundingClientRect();

      const pos = (e.pageX - rect.left) / percentRef.current?.offsetWidth;
      if (frameStart !== null && frameEnd !== null) {
        videoRef.current.currentTime =
          (frameStart + pos * (frameEnd - frameStart)) * (1 / fps);
      } else {
        videoRef.current.currentTime = pos * videoDuration;
      }
    },
    [videoDuration, frameTime, frameStart, frameEnd, fps]
  );

  const onVideoLoadHandler = () => {
    setVideoDuration(videoRef.current.duration);

    // TODO: require refoctoring
    const parts = getFormattedDuration(videoRef.current.duration).split(":");
    const formattedTime = parts[0] + "." + parts[1];

    dispatch(setSuperTrackGlobalVideoDuration(+formattedTime));
    dispatch(setFrameCount(Math.round(videoRef.current.duration / (1 / fps))));
    dispatch(setSuperTrackStartTime(0));
    dispatch(setSuperTrackEndTime(+formattedTime));
  };

  const onTimeUpdateHandler = () => {
    const frameCount = Math.round(videoRef.current.duration / (1 / fps));
    const superTrackStartTime = 0;
    let position =
      Math.round(
        (videoRef.current.currentTime - superTrackStartTime * 60) / (1 / fps)
      ) * 0.5;

    if (position >= frameCount) {
      position = frameCount * 0.5 - superTrackStartTime * 60;
    }
    if (posRef.current) posRef.current.style.left = `${position - 5}px`;
  };

  const onMerge = useCallback(async () => {
    setMenuVisible(false);

    if (!selectedSuperTrackId) {
      alert(
        "Please select new super track. And make sure to click rectangle not track id."
      );
      return;
    }

    if (trackId === selectedSuperTrackId) {
      alert(
        "You've selected the target super track. Please select another one."
      );
      return;
    }

    if (
      hasIntersection({
        targetId: trackId,
        superTrackId: selectedSuperTrackId,
      })
    ) {
      // Instead of window.confirm, show the Bootstrap modal
      setPendingMergeAction({
        videoId,
        trackId,
        selectedSuperTrackId,
      });
      setShowConfirmModal(true);
      return;
    }

    merge(videoId, trackId, selectedSuperTrackId);
  }, [trackId, selectedSuperTrackId, videoId, superTracks, candidates]);

  const handleConfirmMerge = () => {
    setShowConfirmModal(false);
    if (pendingMergeAction) {
      const { videoId, trackId, selectedSuperTrackId } = pendingMergeAction;
      merge(videoId, trackId, selectedSuperTrackId);
      setPendingMergeAction(null);
    }
  };

  const handleCancelMerge = () => {
    setShowConfirmModal(false);
    setPendingMergeAction(null);
  };

  const onCut = async (mode) => {
    if (
      [CUT_MODES.CUT, CUT_MODES.CUT_OFF_RIGHT, CUT_MODES.CUT_OFF_LEFT].includes(
        mode
      )
    ) {
      cut(
        videoId,
        selectedSuperTrackId || trackId,
        selectedTrack?.track_id,
        currentFrame,
        mode
      );
    }
  };

  // const onSubstitute = async () => {
  //   substitute(videoId, selectedSuperTrackId);
  //   saveAnswer(selectedSuperTrackId);
  // };

  useEffect(() => {
    async function cutSuperTrackFunc() {
      if (isCutting.current) {
        // Create a super track
        const newSuperTrack = await matchApi.createSuperTrack(videoId);
        if (!newSuperTrack) return;

        // filter tracks which need to be moved to a new super track
        // TODO: filter tracks correctly
        const selectedSuperTrack = superTracks[selectedSuperTrackId];
        if (!selectedSuperTrack || !selectedSuperTrack.tracks || !Array.isArray(selectedSuperTrack.tracks)) {
          return; // Skip if supertrack is empty or invalid
        }
        const filteredTracks = selectedSuperTrack.tracks.filter(
          (t, index) => {
            return t.frame_start > selectedTrack.frame_start;
          }
        );
        const filteredTrackIds = filteredTracks.map((t) => t.track_id);
        // Move tracks
        replace(
          videoId,
          selectedSuperTrackId,
          newSuperTrack.super_track_uid,
          filteredTrackIds
        );
        // setIsCutting(false);
        isCutting.current = false;
        dispatch(
          cutSuperTrack({
            superTrackId: selectedSuperTrackId,
            newSuperTrack,
            trackIds: filteredTrackIds,
          })
        );
      }
    }
    cutSuperTrackFunc();
  }, [superTracks, selectedTrack, selectedSuperTrackId]);

  const onToggleShowAll = () => {
    setShowAll(!showAll);
  };

  useEffect(() => {
    realtimeSync.current = realTimeSync;
  }, [realTimeSync]);

  useEffect(() => {
    if (isCutActive) {
      videoRef.current?.pause();
    }
  }, [isCutActive]);

  useEffect(() => {
    if (isCutActive) {
      videoRef.current.currentTime = cutFrame * (1 / fps);
    }
  }, [cutFrame, fps, isCutActive]);

  useEffect(() => {
    if (!superTracks) return;
    const currentSuperTrack = superTracks[trackId];
    const tracks = currentSuperTrack?.tracks || [];
    const frames = Object.keys(newBoxes);

    const result = [];
    if (frames && frames.length > 0) {
      let start = frames[0];
      let end = frames[0];

      for (let i = 1; i < frames.length; i++) {
        // Check if the current frame is consecutive to the previous one
        if (frames[i] === end + 1) {
          end = frames[i]; // Extend the range
        } else {
          // Push the current range to the result
          result.push({ frame_start: start, frame_end: end });
          start = frames[i]; // Start a new range
          end = frames[i]; // Set the end to the new start
        }
      }

      // Push the last range to the result
      result.push({ frame_start: start, frame_end: end });
    }
    const newProgressTracks = [...tracks, ...result];
    if (!isEqual(progressTracks, newProgressTracks))
      setProgressTracks(newProgressTracks);
  }, [superTracks, newBoxes]);

  const handleGamePause = (type) => {
    if (type === "ball_out") {
      if (gamePausedOut) {
        setPauseList([
          ...pauseList,
          [
            Math.round(gamePausedOut / frameTime),
            Math.round(videoRef.current.currentTime / frameTime),
            type,
          ],
        ]);
      }

      setGamePausedOut(gamePausedOut ? null : videoRef.current.currentTime);
    } else if (type === "inactive_ball") {
      if (gamePausedInactive) {
        setPauseList([
          ...pauseList,
          [
            Math.round(gamePausedInactive / frameTime),
            Math.round(videoRef.current.currentTime / frameTime),
            type,
          ],
        ]);
      }

      setGamePausedInactive(
        gamePausedInactive ? null : videoRef.current.currentTime
      );
    }
  };

  useEffect(() => {
    if (taskType === "ball_supertrack") {
      setRealTimeSync(false);
    }
  }, [taskType]);

  useEffect(() => {
    if (currentSuperTrack?.jersey) {
      setJersey(currentSuperTrack.jersey);
    }
  }, [currentSuperTrack]);

  return (
    <>
      {dataLoaded ? (
        <div>
          <Row className="tw-justify-center tw-py-2 tw-pt-4">
            <Col
              xs={11}
              className="tw-flex sm:tw-justify-between tw-flex-col-reverse sm:tw-flex-row"
            >
              <div className="tw-flex-1 tw-flex tw-gap-2 tw-items-center tw-flex-wrap">
                <span className="tw-mx-1 tw-my-1">
                  Step:{" "}
                  <input
                    className="super-track-markup-toolbar__step"
                    type="text"
                    pattern="[0-9]*"
                    value={step}
                    onChange={onStepChange}
                  />
                </span>
                <Switch
                  label="Interpolation:"
                  checked={interpol}
                  reverse={true}
                  onChange={setInterpol}
                />
                <Switch
                  label="Overwrite:"
                  checked={overwriteMode}
                  disabled={!interpol}
                  reverse={true}
                  onChange={setOverwriteMode}
                />
                <Button
                  variant="success"
                  size="sm"
                  className="mx-1 my-1"
                  onClick={handleNextMissed}
                >
                  Next Missed
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="mx-1 my-1"
                  onClick={handleNextMissedStep}
                >
                  Next Missed Step / W
                </Button>
                <Switch
                  label="Auto Zoom:"
                  checked={autoZoom}
                  reverse={true}
                  onChange={setAutoZoom}
                />
                <Switch
                  label="Real-time Sync:"
                  checked={realTimeSync}
                  reverse={true}
                  onChange={setRealTimeSync}
                />
                <Switch
                  label="Auto Next:"
                  checked={autoNext}
                  reverse={true}
                  onChange={setAutoNext}
                />
              </div>
              <div>
                <Button
                  variant="success"
                  size="sm"
                  className="mx-1 my-1"
                  disabled={isStoring}
                  onClick={() => store()}
                >
                  {isStoring ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Storing
                    </>
                  ) : (
                    "Store / Ctrl - S"
                  )}
                </Button>
                {taskId ? (
                  <Button
                    variant="success"
                    size="sm"
                    className="mx-1 my-1"
                    disabled={isSaving}
                    onClick={saveAnswer}
                  >
                    {isSaving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />{" "}
                        Saving
                      </>
                    ) : (
                      "Save Answer"
                    )}
                  </Button>
                ) : null}
              </div>
            </Col>
          </Row>
          <Row className="tw-justify-center">
            <Col xs={11} className="tw-flex tw-justify-between">
              <div>
                <span className="tw-mr-2">
                  Frame:{" "}
                  <input
                    className="super-track-markup-video__frame-input mx-1"
                    type="text"
                    pattern="[0-9]*"
                    value={currentFrame}
                    onChange={onFrameInputChange}
                  />
                </span>
                {/* <span className="mx-2">Time: {currentTime.toFixed(3)}s</span> */}
                <span className="mx-2">Total Gaps: {gapCounts}</span>
                <span className="mx-2">FPS: {fps.toFixed(3)}</span>
                <span className="mx-2">
                  Frame Time: {frameTime.toFixed(3)}s
                </span>
              </div>
              <div>
                <div className="mx-2">
                  Jersey #:{" "}
                  <Form.Control
                    type="text"
                    placeholder="Jersey #"
                    size="sm"
                    className="tw-inline-block tw-w-24 tw-mr-2"
                    value={jersey}
                    onChange={onJerseyChange}
                    onBlur={onJerseyUpdate}
                  />
                </div>

                {(taskType === "ball_supertrack" || allowPauseEditing) && (
                  <div className="tw-flex tw-gap-2">
                    <MyButton
                      className={cn(
                        "tw-py-0 tw-h-full",
                        gamePausedOut ? "tw-animate-bg-transition" : "",
                        gamePausedInactive ? "tw-bg-gray-400" : ""
                      )}
                      size="medium"
                      shape="rect"
                      disabled={gamePausedInactive}
                      icon={
                        !gamePausedOut ? (
                          <FontAwesomeIcon icon="fa-solid fa-stopwatch" />
                        ) : (
                          <FontAwesomeIcon icon="fa-solid fa-stopwatch" beat />
                        )
                      }
                      onClick={() => handleGamePause("ball_out")}
                      title="Ball Out (T)"
                    >
                      Ball Out
                    </MyButton>

                    <MyButton
                      className={cn(
                        "tw-py-0 tw-h-full",
                        gamePausedInactive ? "tw-animate-bg-transition" : "",
                        gamePausedOut ? "tw-bg-gray-400" : ""
                      )}
                      size="medium"
                      shape="rect"
                      disabled={gamePausedOut}
                      icon={
                        !gamePausedInactive ? (
                          <FontAwesomeIcon icon="fa-solid fa-stopwatch" />
                        ) : (
                          <FontAwesomeIcon icon="fa-solid fa-stopwatch" beat />
                        )
                      }
                      onClick={() => handleGamePause("inactive_ball")}
                      title="Inactive Ball (G)"
                    >
                      Inactive Ball
                    </MyButton>
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row className="tw-justify-center tw-py-2">
            <Col className="super-track-markup-progress" xs={11}>
              <div className="tw-relative triangle-caret-container">
                {totalFrames ? (
                  <>
                    <div
                      className="triangle-caret"
                      style={{
                        left:
                          frameStart !== null && frameEnd !== null
                            ? (videoDomWidth * (currentFrame - frameStart)) /
                            (frameEnd - frameStart)
                            : (currentFrame * videoDomWidth) / totalFrames,
                      }}
                    ></div>
                    {(taskType === "ball_supertrack" || allowPauseEditing) &&
                      pauseList.map((pause, index) => (
                        <div
                          key={index}
                          className="tw-absolute -tw-translate-x-1/2 -tw-translate-y-3 tw-text-[10px]"
                          style={{
                            left:
                              frameStart !== null && frameEnd !== null
                                ? (videoDomWidth *
                                  (Math.abs(pause[1] + pause[0]) / 2 -
                                    frameStart)) /
                                (frameEnd - frameStart)
                                : ((Math.abs(pause[1] + pause[0]) / 2 -
                                  frameStart) *
                                  videoDomWidth) /
                                totalFrames,
                          }}
                        >
                          <HourGlassButton
                            onClick={() => {
                              setPauseList([
                                ...pauseList.filter((_, i) => i !== index),
                              ]);
                            }}
                          />
                        </div>
                      ))}
                  </>
                ) : null}
              </div>
              <ProgressBar
                videoDomWidth={videoDomWidth}
                progressTracks={progressTracks}
                pausedList={pauseList}
                frameStart={frameStart ?? 0}
                frameEnd={frameEnd || totalFrames}
                percentRef={percentRef}
                onProgressClick={onProgressClick}
              />
            </Col>
          </Row>
          <Row className="tw-relative tw-justify-center">
            <Col xs={11} className="tw-relative">
              <VideoPlayer
                ref={videoRef}
                src={videoPath}
                fps={fps}
                step={step}
                frameStep={frameStep}
                videoId={videoId}
                startTime={frameStart !== null ? frameStart * (1 / fps) : null}
                endTime={frameEnd !== null ? frameEnd * (1 / fps) : null}
                tracks={memoizedBoxes}
                colors={{
                  ...colors,
                  main: "yellow",
                }}
                ids={trackIds}
                autoZoom={autoZoom}
                focusedTrackId="main"
                idVisible={taskType === "ball_supertrack" ? false : true}
                trackResizable={allowed ? true : false}
                showLastTrack={"main"} // trackId of main track which needs to be visible whole time.
                tempRectColor="red"
                zoomButtonsVisible={true}
                pbRate={pbRate}
                onFrameChange={onFrameChange}
                keyDownHandler={keyDownHandler}
                onMouseDownHandler={onMouseDownHandler}
                onMouseMoveHandler={onMouseMoveHandler}
                onMouseUpHandler={onMouseUpHandler}
                onStageScaleChange={onStageScaleChange}
                onStagePosChange={onStagePosChange}
                onRatioChange={onRatioChange}
                onVideoDomSizeChange={onVideoDomSizeChange}
                onVideoSizeChange={onVideoSizeChange}
                onVideoLoadHandler={onVideoLoadHandler}
                onTimeUpdateHandler={onTimeUpdateHandler}
                onResizeTrack={onResizeRect}
                onSetInitialY={onSetInitialY}
              >
                <Rect ref={newRectRef} name="newMarkup" />
              </VideoPlayer>
              {!allowed && (
                <div className="tw-absolute tw-left-0 tw-top-0 tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center tw-text-red-600 tw-text-9xl tw-pointer-events-none">
                  <FontAwesomeIcon icon="fa-solid fa-stopwatch" beat />
                </div>
              )}
              <ControlledMenu
                ref={menuRef}
                state={menuVisible ? "open" : "closed"}
                direction="right"
                onClose={() => setMenuVisible(false)}
                onContextMenu={(e) => e.preventDefault()}
              >
                <MenuItem onClick={() => onMerge()}>Merge</MenuItem>
                {(selectedSuperTrackId === trackId ||
                  selectedSuperTrackId === undefined) && (
                    <SubMenu label="Cut">
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT)}>
                        Cut
                      </MenuItem>
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT_OFF_RIGHT)}>
                        Cut Off Right
                      </MenuItem>
                      <MenuItem onClick={() => onCut(CUT_MODES.CUT_OFF_LEFT)}>
                        Cut Off Left
                      </MenuItem>
                    </SubMenu>
                  )}
                {/* <MenuItem onClick={() => onSubstitute()}>Substitute</MenuItem> */}
              </ControlledMenu>
            </Col>
          </Row>
          <Row className="tw-justify-center">
            <Col xs={11}>
              <Form.Check
                className={`switch ${showAll ? "on" : "off"}`}
                reverse
                inline
                type="switch"
                label="Show All:"
                onChange={onToggleShowAll}
              />
              <Form.Check
                className={`switch ${showPanel ? "on" : "off"}`}
                reverse
                inline
                type="switch"
                label="Show Panel:"
                onChange={() => setShowPanel(!showPanel)}
              />
              <span className="tw-mx-1 tw-my-1">
                Search Range(seconds):{" "}
                <input
                  className="super-track-markup-toolbar__step"
                  type="text"
                  pattern="[0-9]*"
                  value={String(searchRange)}
                  onChange={onSearchRangeChange}
                />
              </span>
            </Col>
          </Row>
          {/* SuperTrack merging interface */}
          {showPanel ? (
            <Row className="tw-justify-center">
              <SuperTracksPanel
                ref={posRef}
                mainSuperTrackId={trackId}
                visibleSuperTrackIds={visibleSuperTrackIds}
                showAll={showAll}
              />
            </Row>
          ) : null}
          <Modal
            show={showConfirmModal}
            onHide={handleCancelMerge}
            backdrop="static" // Prevents closing when clicking outside
            keyboard={false} // Prevents closing with keyboard (like Esc)
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Merge</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              The two supertracks have an intersection. Are you sure you want to
              merge them?
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelMerge}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmMerge}>
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      ) : (
        <Preloader className="tw-m-auto" />
      )}
    </>
  );
}
